import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from "react";
import handleService from "../services/apiService.ts";
import { onPlanBlocked } from "../utils/planEvents.ts";
import { invalidarPlanLimitsCache } from "../hooks/usePlanLimits.ts";

type PlanoTipo = "free" | "premium" | "premiumPlus";

type PlanoContextType = {
  plano: PlanoTipo;
  setPlano: (p: PlanoTipo) => void;
  isPremium: boolean;
  isPremiumPlus: boolean;
  isLoading: boolean;
  isPlanActive: boolean;
  pago: boolean;
  dataExpiracao: string | null;
  planoNome: string | null;
  planoModeloId: string | null;
  billingType: string | null;
  ciclo: string | null;
  cancelado: boolean;
  refreshPlano: () => void;
};

const PlanoContext = createContext<PlanoContextType | undefined>(undefined);

export const PlanoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [plano, setPlanoState] = useState<PlanoTipo>(() => {
    const saved = localStorage.getItem("plano");
    if (saved === "premium" || saved === "premiumPlus") return saved;
    return "free";
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isPlanActive, setIsPlanActive] = useState(true);
  const [pago, setPago] = useState(false);
  const [dataExpiracao, setDataExpiracao] = useState<string | null>(null);
  const [planoNome, setPlanoNome] = useState<string | null>(null);
  const [planoModeloId, setPlanoModeloId] = useState<string | null>(null);
  const [billingType, setBillingType] = useState<string | null>(null);
  const [ciclo, setCiclo] = useState<string | null>(null);
  const [cancelado, setCancelado] = useState(false);

  const fetchPlano = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      setIsPlanActive(true);
      return;
    }

    try {
      const res = await handleService({ endpoint: "/plano/meu-plano" });
      const data = res?.data;

      // Quem decide o bloqueio e o backend, em Empresa.isAcessoBloqueado(): e la
      // que mora a folga de fim de semana (vencimento na sexta so bloqueia na
      // segunda) e o fuso de Brasilia. Recalcular aqui significaria manter a
      // mesma regra de calendario em dois idiomas, e divergir no primeiro ajuste.
      //
      // O calculo antigo fica como fallback para a janela de deploy: a Vercel
      // publica o frontend a cada push, enquanto a API sobe a mao. Sem ele, entre
      // os dois deploys o campo vem undefined e `!undefined` liberaria todo mundo.
      const active = typeof data?.acessoBloqueado === "boolean"
        ? !data.acessoBloqueado
        : !(data?.dataExpiracao
            ? new Date(data.dataExpiracao + "T23:59:59") < new Date()
            : false);
      setIsPlanActive(active);
      setPago(data?.pago || false);
      setDataExpiracao(data?.dataExpiracao || null);
      setPlanoNome(data?.planoNome || null);
      setPlanoModeloId(data?.planoModeloId || null);
      setBillingType(data?.billingType || null);
      setCiclo(data?.ciclo || null);
      setCancelado(data?.cancelado || false);
    } catch {
      setIsPlanActive(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlano();
  }, [fetchPlano]);

  useEffect(() => {
    const unsubscribe = onPlanBlocked(() => {
      setIsPlanActive(false);
      setPago(false);
    });
    return unsubscribe;
  }, []);

  const setPlano = (novoPlano: PlanoTipo) => {
    setPlanoState(novoPlano);
    localStorage.setItem("plano", novoPlano);
  };

  const { isPremium, isPremiumPlus } = useMemo(() => {
    return {
      isPremium: plano === "premium",
      isPremiumPlus: plano === "premiumPlus",
    };
  }, [plano]);

  return (
    <PlanoContext.Provider value={{
      plano, setPlano, isPremium, isPremiumPlus,
      isLoading, isPlanActive, pago, dataExpiracao, planoNome, planoModeloId, billingType, ciclo,
      cancelado,
      // usePlanLimits cacheia GET /plano/limites por 5min; toda vez que o plano
      // e reconsultado (troca, reativacao, cancelamento) o cache precisa cair
      // junto, senao a tela continuaria mostrando o limite do plano antigo.
      refreshPlano: () => {
        invalidarPlanLimitsCache();
        fetchPlano();
      },
    }}>
      {children}
    </PlanoContext.Provider>
  );
};

export const usePlanoContext = () => {
  const context = useContext(PlanoContext);
  if (!context) {
    throw new Error("usePlanoContext deve ser usado dentro de PlanoProvider");
  }
  return context;
};
